import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Form, Modal, Pagination, Table } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const Company = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [status, setStatus] = useState(1);

  const loadCompanies = async () => {
    setLoading(true);
    const res = await API.get('/api/company/?p=0&p_size=1000');
    const { success, message, data } = res.data;
    if (success) {
      setCompanies(data || []);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setName('');
    setStatus(1);
    setShowModal(true);
  };

  const openEdit = (company) => {
    setEditingId(company.id);
    setName(company.name);
    setStatus(company.status);
    setShowModal(true);
  };

  const submit = async () => {
    if (!name.trim()) {
      showError(t('org.company.name_required'));
      return;
    }
    const payload = { name: name.trim(), status };
    let res;
    if (editingId) {
      res = await API.put('/api/company/', { ...payload, id: editingId });
    } else {
      res = await API.post('/api/company/', payload);
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(editingId ? t('org.update_success') : t('org.create_success'));
      setShowModal(false);
      loadCompanies();
    } else {
      showError(message);
    }
  };

  const onDelete = async (id) => {
    const res = await API.delete(`/api/company/${id}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('org.delete_success'));
      loadCompanies();
    } else {
      showError(message);
    }
  };

  const onPageChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  const totalPages = Math.max(1, Math.ceil(companies.length / ITEMS_PER_PAGE));
  const pageData = companies.slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE);

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>{t('org.company.title')}</Card.Header>
          <Button size='small' onClick={openAdd} loading={loading}>{t('org.company.add')}</Button>
          <Button size='small' onClick={() => loadCompanies()} loading={loading}>{t('org.refresh')}</Button>
          <Table basic='very' compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>{t('org.company.name')}</Table.HeaderCell>
            <Table.HeaderCell>{t('org.status')}</Table.HeaderCell>
            <Table.HeaderCell>{t('org.created_time')}</Table.HeaderCell>
            <Table.HeaderCell>{t('org.actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {pageData.map((company) => (
            <Table.Row key={company.id}>
              <Table.Cell>{company.id}</Table.Cell>
              <Table.Cell>{company.name}</Table.Cell>
              <Table.Cell>{company.status === 1 ? t('org.enabled') : t('org.disabled')}</Table.Cell>
              <Table.Cell>{new Date(company.created_time * 1000).toLocaleString()}</Table.Cell>
              <Table.Cell>
                <Button size='tiny' onClick={() => openEdit(company)}>{t('org.edit')}</Button>
                <Button size='tiny' negative onClick={() => onDelete(company.id)}>{t('org.delete')}</Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
          <Pagination activePage={activePage} onPageChange={onPageChange} size='small' totalPages={totalPages} />
        </Card.Content>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} size='tiny'>
        <Modal.Header>{editingId ? t('org.company.edit') : t('org.company.add')}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input label={t('org.company.name')} placeholder={t('org.company.name_placeholder')} value={name} onChange={(e, { value }) => setName(value)} />
            <Form.Dropdown label={t('org.status')} selection value={status} options={[{ key: 1, text: t('org.enabled'), value: 1 }, { key: 2, text: t('org.disabled'), value: 2 }]} onChange={(e, { value }) => setStatus(value)} />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowModal(false)}>{t('org.cancel')}</Button>
          <Button positive onClick={submit}>{t('org.save')}</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default Company;
